/**
 * @fileoverview Zod validation schemas for authentication and user management
 * @module features/auth/schemas/authSchemas
 */

import { z } from 'zod';

/**
 * Branded type for User IDs to ensure type safety across the application.
 * Prevents accidentally passing wrong ID types to functions.
 */
export const UserIdSchema = z.string().uuid().brand<'UserId'>();
export type UserId = z.infer<typeof UserIdSchema>;

/**
 * Branded type for Profile IDs to ensure type safety.
 */
export const ProfileIdSchema = z.string().uuid().brand<'ProfileId'>();
export type ProfileId = z.infer<typeof ProfileIdSchema>;

/**
 * Schema for authentication provider types.
 * Defines the supported authentication methods in the application.
 */
export const AuthProviderSchema = z.enum(['wallet', 'google', 'twitter']);
export type AuthProvider = z.infer<typeof AuthProviderSchema>;

/**
 * Schema for user types that determine user capabilities and features.
 * Each type has different onboarding flows and dashboard features.
 */
export const UserTypeSchema = z.enum(['regular', 'kol', 'project_creator', 'kol_manager']);
export type UserType = z.infer<typeof UserTypeSchema>;

/**
 * Schema for Supabase user session data validation.
 * Used to validate OAuth responses and user session information.
 */
export const SupabaseUserSchema = z.object({
  id: UserIdSchema,
  email: z.string().email().optional(),
  user_metadata: z.object({
    avatar_url: z.string().url().optional(),
    email: z.string().email().optional(),
    full_name: z.string().optional(),
    name: z.string().optional(),
    provider_id: z.string().optional(),
    sub: z.string().optional(),
  }).optional(),
  app_metadata: z.object({
    provider: AuthProviderSchema.optional(),
    providers: z.array(z.string()).optional(),
  }).optional(),
  aud: z.string().optional(),
  created_at: z.string().datetime().optional(),
  updated_at: z.string().datetime().optional(),
});
export type SupabaseUser = z.infer<typeof SupabaseUserSchema>;

/**
 * Schema for user profile data stored in the profiles table.
 * This is the main user profile that extends Supabase auth.users.
 */
export const UserProfileSchema = z.object({
  id: z.string().uuid().optional(), // Made optional for flexibility
  user_id: z.string().uuid().nullable().optional(), // Nullable for Dynamic.xyz users (no Supabase Auth)
  dynamic_user_id: z.string().nullable().optional(), // Dynamic.xyz user ID
  user_type: UserTypeSchema.nullable().optional(), // Made nullable and optional
  email: z.string().email().nullable().optional(),
  full_name: z.string().nullable().optional(),
  avatar_url: z.string().url().nullable().optional(),
  auth_provider: AuthProviderSchema.nullable().optional(),
  wallet_address: z.string().regex(/^0x[a-fA-F0-9]{40}$/, 'Invalid wallet address').nullable().optional(),
  onboarding_completed: z.boolean().optional().default(false),
  onboarding_step: z.string().nullable().optional(),
  x_verified: z.boolean().optional().default(false),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
  
  // KOL-specific fields - all optional and nullable
  specialization: z.array(z.enum(['DeFi', 'NFTs', 'GameFi', 'Layer1', 'Layer2', 'Memecoins', 'Trading', 'Staking'])).nullable().optional(),
  twitter_handle: z.string().nullable().optional(),
  youtube_channel: z.string().nullable().optional(),
  telegram_channel: z.string().nullable().optional(),
  follower_count: z.number().int().min(0).nullable().optional(),
  
  // Project creator-specific fields - all optional and nullable
  project_name: z.string().nullable().optional(),
  project_description: z.string().nullable().optional(),
  website_url: z.string().nullable().optional(),
  whitepaper_url: z.string().nullable().optional(),
  token_symbol: z.string().nullable().optional(),
  blockchain: z.enum(['ethereum', 'polygon', 'bsc', 'arbitrum', 'optimism', 'avalanche', 'fantom', 'solana']).nullable().optional(),
  funding_stage: z.enum(['pre_seed', 'seed', 'series_a', 'series_b', 'series_c', 'public']).nullable().optional(),
});
export type UserProfile = z.infer<typeof UserProfileSchema>;

/**
 * Schema for creating a new user profile.
 * Used when a user completes the onboarding process.
 */
export const CreateUserProfileSchema = z.object({
  user_id: UserIdSchema,
  user_type: UserTypeSchema,
  email: z.string().email().optional(),
  full_name: z.string().min(1, 'Full name is required').optional(),
  avatar_url: z.string().url().optional(),
  auth_provider: AuthProviderSchema,
  wallet_address: z.string().regex(/^0x[a-fA-F0-9]{40}$/, 'Invalid wallet address').optional(),
  onboarding_completed: z.boolean().default(false),
  onboarding_step: z.string().nullable().optional(),
  x_verified: z.boolean().optional(),
  
  // KOL-specific fields
  specialization: z.array(z.enum(['DeFi', 'NFTs', 'GameFi', 'Layer1', 'Layer2', 'Memecoins', 'Trading', 'Staking'])).nullable().optional(),
  twitter_handle: z.string().nullable().optional(),
  youtube_channel: z.string().nullable().optional(),
  telegram_channel: z.string().nullable().optional(),
  follower_count: z.number().int().min(0).nullable().optional(),
  
  // Project creator-specific fields
  project_name: z.string().nullable().optional(),
  project_description: z.string().nullable().optional(),
  website_url: z.string().nullable().optional(),
  whitepaper_url: z.string().nullable().optional(),
  token_symbol: z.string().nullable().optional(),
  blockchain: z.enum(['ethereum', 'polygon', 'bsc', 'arbitrum', 'optimism', 'avalanche', 'fantom', 'solana']).nullable().optional(),
  funding_stage: z.enum(['pre_seed', 'seed', 'series_a', 'series_b', 'series_c', 'public']).nullable().optional(),
});
export type CreateUserProfile = z.infer<typeof CreateUserProfileSchema>;

/**
 * Schema for updating user profile data.
 * All fields are optional to allow partial updates.
 */
export const UpdateUserProfileSchema = z.object({
  user_type: UserTypeSchema.optional(),
  email: z.string().email().optional(),
  full_name: z.string().min(1, 'Full name is required').optional(),
  avatar_url: z.string().url().optional(),
  wallet_address: z.string().regex(/^0x[a-fA-F0-9]{40}$/, 'Invalid wallet address').optional(),
  onboarding_completed: z.boolean().optional(),
  onboarding_step: z.string().nullable().optional(),
  x_verified: z.boolean().optional(),
  
  // KOL-specific fields
  specialization: z.array(z.enum(['DeFi', 'NFTs', 'GameFi', 'Layer1', 'Layer2', 'Memecoins', 'Trading', 'Staking'])).nullable().optional(),
  twitter_handle: z.string().nullable().optional(),
  youtube_channel: z.string().nullable().optional(),
  telegram_channel: z.string().nullable().optional(),
  follower_count: z.number().int().min(0).nullable().optional(),
  
  // Project creator-specific fields
  project_name: z.string().nullable().optional(),
  project_description: z.string().nullable().optional(),
  website_url: z.string().nullable().optional(),
  whitepaper_url: z.string().nullable().optional(),
  token_symbol: z.string().nullable().optional(),
  blockchain: z.enum(['ethereum', 'polygon', 'bsc', 'arbitrum', 'optimism', 'avalanche', 'fantom', 'solana']).nullable().optional(),
  funding_stage: z.enum(['pre_seed', 'seed', 'series_a', 'series_b', 'series_c', 'public']).nullable().optional(),
});
export type UpdateUserProfile = z.infer<typeof UpdateUserProfileSchema>;

/**
 * Schema for authentication state management.
 * Represents the current authentication status of the user.
 */
export const AuthStateSchema = z.object({
  isAuthenticated: z.boolean(),
  user: SupabaseUserSchema.nullable(),
  profile: UserProfileSchema.nullable(),
  loading: z.boolean(),
  error: z.string().nullable(),
});
export type AuthState = z.infer<typeof AuthStateSchema>;

/**
 * Schema for OAuth callback URL parameters.
 * Used to validate URL parameters after OAuth redirects.
 */
export const OAuthCallbackSchema = z.object({
  access_token: z.string().optional(),
  expires_in: z.string().optional(),
  refresh_token: z.string().optional(),
  token_type: z.string().optional(),
  type: z.string().optional(),
  error: z.string().optional(),
  error_description: z.string().optional(),
});
export type OAuthCallback = z.infer<typeof OAuthCallbackSchema>;

/**
 * Schema for wallet connection data.
 * Used to validate wallet address and type information.
 */
export const WalletConnectionSchema = z.object({
  address: z.string().regex(/^0x[a-fA-F0-9]{40}$/, 'Invalid wallet address'),
  chainId: z.number().positive(),
  isConnected: z.boolean(),
  connector: z.object({
    id: z.string(),
    name: z.string(),
    type: z.string(),
  }).optional(),
});
export type WalletConnection = z.infer<typeof WalletConnectionSchema>;

/**
 * Schema for user wallets table data.
 * Supports multiple wallets per user for enhanced flexibility.
 */
export const UserWalletSchema = z.object({
  id: z.string().uuid(),
  user_id: z.string().uuid().nullable().optional(), // Nullable for Dynamic.xyz users
  wallet_address: z.string().regex(/^0x[a-fA-F0-9]{40}$/, 'Invalid wallet address'),
  wallet_type: z.string().min(1, 'Wallet type is required'),
  is_primary: z.boolean(),
  created_at: z.string().datetime(),
});
export type UserWallet = z.infer<typeof UserWalletSchema>;

/**
 * Schema for creating new wallet connections.
 */
export const CreateUserWalletSchema = z.object({
  user_id: z.string().uuid().nullable().optional(), // Nullable for Dynamic.xyz users
  wallet_address: z.string().regex(/^0x[a-fA-F0-9]{40}$/, 'Invalid wallet address'),
  wallet_type: z.string().min(1, 'Wallet type is required'),
  is_primary: z.boolean().default(false),
});
export type CreateUserWallet = z.infer<typeof CreateUserWalletSchema>;